import {FullGroup, groupName} from '@shared/models/api_models';

import {createDataStore} from '@shared_web/lib/data_store';

import {setDoorIds} from '@mavismart_frontend/stores/door_ids_store';

const groupsStore = createDataStore<FullGroup[] | undefined>(undefined);

export const useGroups = groupsStore.useData;
export const clearGroups = () => groupsStore.setData(undefined);

export const addGroup = (group: FullGroup) => {
  const currentGroups = groupsStore.getData() ?? [];
  const updatedGroups = [...currentGroups, group];
  setGroups(updatedGroups);
};

export const setGroups = (groups: FullGroup[] | undefined) => {
  if (groups === undefined) {
    groupsStore.setData(undefined);
    setDoorIds(undefined);
    return;
  }

  groups.sort((a, b) => {
    // Group be parent group
    const parentGroupA = a.group.parentGroupId
      ? groups.find(g => g.group.id === a.group.parentGroupId)
      : undefined;
    const parentGroupB = b.group.parentGroupId
      ? groups.find(g => g.group.id === b.group.parentGroupId)
      : undefined;
    const parentGroupNameA = parentGroupA ? groupName(parentGroupA) : groupName(a);
    const parentGroupNameB = parentGroupB ? groupName(parentGroupB) : groupName(b);
    const comparisonByParent = parentGroupNameA.localeCompare(parentGroupNameB);
    if (comparisonByParent !== 0) {
      return comparisonByParent;
    }
    // Sort by:
    // 1. Parent group must be first
    // 2. Then by group id
    if (a.group.parentGroupId === undefined) {
      return -1;
    }
    if (b.group.parentGroupId === undefined) {
      return 1;
    }
    return a.group.id.localeCompare(b.group.id);
  });
  groupsStore.setData(groups);

  const uniqueDoors = new Set<string>();
  for (const {doorIds} of groups) {
    for (const doorId of doorIds) {
      uniqueDoors.add(doorId);
    }
  }
  setDoorIds(Array.from(uniqueDoors));
};

// TODO: for now it is done with the list-all-doors because no perf issue
// export const fetchGroups = () => {
//   apiCall('POST /list-groups', {})
//     .then(response => {
//       setGroups(response.groups);
//     })
//     .catch(notifyError);
// };

export const addDoorToGroup = async (doorId: string, groupId: string) => {
  // 1. find the group with the groupid
  const groups = groupsStore.getData();
  if (!groups) {
    return;
  }
  const fullGroup = groups.find(g => g.group.id === groupId);
  if (!fullGroup) {
    return;
  }

  // 2. add the door to the group
  const updatedGroup: FullGroup = {
    ...fullGroup,
    doorIds: Array.from(new Set([...fullGroup.doorIds, doorId])),
  };

  // 3. update the store
  const updatedGroups = groups.map(g => (g.group.id === groupId ? updatedGroup : g));
  setGroups(updatedGroups);
};

export const removeDoorFromGroup = async (doorId: string, groupId: string) => {
  // 1. find the group with the groupid
  const groups = groupsStore.getData();
  if (!groups) {
    return;
  }
  const fullGroup = groups.find(g => g.group.id === groupId);
  if (!fullGroup) {
    return;
  }

  // 2. remove the door from the group
  const updatedGroup: FullGroup = {
    ...fullGroup,
    doorIds: fullGroup.doorIds.filter(id => id !== doorId),
  };

  // 3. update the store
  const updatedGroups = groups.map(g => (g.group.id === groupId ? updatedGroup : g));
  setGroups(updatedGroups);
};

export const renameGroup = (groupId: string, newName: string) => {
  const groups = groupsStore.getData();
  if (!groups) {
    return;
  }
  const updatedGroups = groups.map(g =>
    g.group.id === groupId ? {...g, group: {...g.group, name: newName}} : g
  );
  setGroups(updatedGroups);
};
